import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle2, XCircle, AlertCircle, RefreshCw, Database, Layers, Activity } from 'lucide-react';
import { checkSystemHealth } from '@/lib/platformAdminApi';
import { toast } from 'sonner';

const getStatusIcon = (status) => {
  switch (status) {
    case 'operational':
    case 'connected':
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    case 'degraded':
      return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    case 'disconnected':
    case 'error':
      return <XCircle className="h-5 w-5 text-red-500" />;
    default:
      return <AlertCircle className="h-5 w-5 text-gray-400" />;
  }
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'operational':
    case 'connected':
      return <Badge className="bg-green-100 text-green-700 border-green-200">Healthy</Badge>;
    case 'degraded':
      return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Degraded</Badge>;
    case 'disconnected':
    case 'error':
      return <Badge className="bg-red-100 text-red-700 border-red-200">Error</Badge>;
    default:
      return <Badge variant="secondary">Unknown</Badge>;
  }
};

export const SystemHealthCard = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const response = await checkSystemHealth();
      if (response.success && response.agent_run) {
        // Extract health data from Hermes response
        const toolCalls = response.agent_run.tool_calls || [];
        const healthTool = toolCalls.find(t => t.tool_name === 'check_system_health');
        
        if (healthTool && healthTool.result) {
          setHealth(healthTool.result);
        } else {
          // Fallback to mock data structure if tool call parsing fails
          setHealth({
            system_status: 'operational',
            database: 'connected',
            blockchain: 'connected',
            metrics: {
              total_hotels: 0,
              total_staked_hermes: 0,
              active_agents: 0
            }
          });
        }
      }
    } catch (error) {
      console.error('Error fetching system health:', error);
      toast.error('Failed to check system health');
      setHealth(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    // Refresh every 60 seconds
    const interval = setInterval(fetchHealth, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Card className="card-hover-lift" data-testid="system-health-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="heading-font">System Health</CardTitle>
            <CardDescription>Real-time platform status</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchHealth}
            disabled={loading}
            data-testid="refresh-health-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : health ? (
          <div className="space-y-4">
            {/* Overall Status */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                {getStatusIcon(health.system_status)}
                <div>
                  <p className="font-semibold heading-font">Platform Status</p>
                  <p className="text-sm text-muted-foreground capitalize">{health.system_status}</p>
                </div>
              </div>
              {getStatusBadge(health.system_status)}
            </div>

            {/* Service Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Database */}
              <div className="flex items-center gap-3 p-3 bg-card border rounded-lg">
                <Database className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Database</p>
                  <p className="text-xs text-muted-foreground capitalize">{health.database}</p>
                </div>
                {getStatusIcon(health.database)}
              </div>

              {/* Blockchain */}
              <div className="flex items-center gap-3 p-3 bg-card border rounded-lg">
                <Layers className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Blockchain</p>
                  <p className="text-xs text-muted-foreground capitalize">{health.blockchain}</p>
                </div>
                {getStatusIcon(health.blockchain)}
              </div>
            </div>

            {/* Metrics */}
            {health.metrics && (
              <div className="border-t pt-4 space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium">Platform Metrics</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-muted/50 rounded">
                    <p className="text-2xl font-bold heading-font tabular-nums">
                      {health.metrics.total_hotels}
                    </p>
                    <p className="text-xs text-muted-foreground">Hotels</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded">
                    <p className="text-2xl font-bold heading-font tabular-nums">
                      {(health.metrics.total_staked_hermes || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">Staked HERMES</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded">
                    <p className="text-2xl font-bold heading-font tabular-nums">
                      {health.metrics.active_agents}
                    </p>
                    <p className="text-xs text-muted-foreground">Active Agents</p>
                  </div>
                </div>
              </div>
            )}

            {/* Timestamp */}
            {health.timestamp && (
              <p className="text-xs text-muted-foreground text-right">
                Last checked: {new Date(health.timestamp).toLocaleTimeString()}
              </p>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <XCircle className="h-12 w-12 mx-auto text-red-400 mb-3" />
            <p className="text-muted-foreground mb-4">Unable to fetch system health</p>
            <Button onClick={fetchHealth} variant="outline" size="sm">
              Retry
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SystemHealthCard;
